import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock3, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, money } from "../lib/api";
import { Loading } from "../components/Shell";

const useCountdown = (assessment) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!assessment) return undefined;
    const offset = Date.now() - new Date(assessment.server_time).getTime();
    const end = new Date(assessment.expires_at).getTime() + offset;
    const tick = () => setLeft(Math.max(0, Math.round((end - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [assessment]);
  return left;
};

const stop = (e) => e.preventDefault();

const Confetti = () => (
  <div className="confetti" aria-hidden="true">{Array.from({ length: 28 }, (_, i) => <i key={i} style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 7) * 0.12}s`, background: ["#F47721", "#FFB900", "#1B5E9B", "#10B981"][i % 4] }} />)}</div>
);

const PassPreview = ({ slug }) => {
  const [img, setImg] = useState(null);
  const [cert, setCert] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/certificate-preview/${slug}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setImg(url); }).catch(() => setImg(false));
    api.get(`/certifications/${slug}`).then((r) => setCert(r.data)).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [slug]);
  return (
    <div className="pass-preview" data-testid="certificate-preview">
      {img ? <div className="preview-frame"><img src={img} alt="Blurred sample certificate" data-testid="certificate-preview-image" /><span className="preview-lock"><Lock size={14} /> Locked sample</span></div>
        : img === null ? <div className="preview-frame loading-frame">Preparing your certificate preview…</div> : null}
      <p className="muted small">This is a blurred sample. Unlock your certificate to get the real one with your full name, certificate ID and QR code, as a PDF and PNG you can share on LinkedIn.</p>
      <Link className="button primary" data-testid="preview-unlock-button" to={`/dashboard?unlock=${slug}`}>Unlock my certificate{cert ? ` for ${money(cert.certificate_price)}` : ""} <ArrowRight size={17} /></Link>
    </div>
  );
};

const Result = ({ result, slug }) => (
  <main className="result-page" data-testid="assessment-result">
    {result.passed && <Confetti />}
    <div className={result.passed ? "result-icon success" : "result-icon fail"}>{result.passed ? <CheckCircle2 /> : <X />}</div>
    <div className="section-kicker">ASSESSMENT COMPLETE</div><h1>{result.passed ? "Congratulations, you passed!" : "Keep building."}</h1>
    <p className="lead">You scored <strong data-testid="assessment-score">{result.score}%</strong> ({result.correct}/{result.total}){result.passed ? ". Your certificate is ready to unlock." : result.timed_out ? " — time ran out before submission. You can try again from your dashboard." : ` — the pass mark is ${result.pass_score}%. You can try again from your dashboard when you're ready.`}</p>
    {result.passed && <PassPreview slug={slug} />}
    <Link className={result.passed ? "text-link centered-link" : "button primary"} data-testid="assessment-result-dashboard-button" to="/dashboard">Return to dashboard <ArrowRight size={17} /></Link>
  </main>
);

export default function Assessment() {
  const { slug } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submitted = useRef(false);
  const left = useCountdown(assessment);
  useEffect(() => {
    const block = (e) => { if ((e.ctrlKey || e.metaKey) && ["c", "x", "v", "a", "p", "s"].includes(e.key.toLowerCase())) e.preventDefault(); };
    window.addEventListener("keydown", block);
    return () => window.removeEventListener("keydown", block);
  }, []);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api.post(`/assessment/start?slug=${slug}`).then((r) => setAssessment(r.data)).catch((e) => { toast.error(formatError(e)); navigate("/dashboard"); });
  }, [slug, navigate]);
  const submit = useCallback(async () => {
    if (submitted.current) return;
    submitted.current = true; setBusy(true);
    try { setResult((await api.post("/assessment/submit", { session_id: assessment.session_id, answers })).data); }
    catch (e) { submitted.current = false; toast.error(formatError(e)); } finally { setBusy(false); }
  }, [assessment, answers]);
  useEffect(() => { if (left === 0 && assessment) submit(); }, [left, assessment, submit]);
  useEffect(() => { if (result) window.scrollTo(0, 0); }, [result]);
  if (result) return <Result result={result} slug={slug} />;
  if (!assessment) return <Loading label="Preparing your assessment…" />;
  const answered = Object.keys(answers).length;
  const total = assessment.questions.length;
  const confirmSubmit = () => { if (answered < total && !window.confirm(`You have ${total - answered} unanswered question(s). Submit anyway?`)) return; submit(); };
  const mm = String(Math.floor((left ?? 0) / 60)).padStart(2, "0");
  const ss = String((left ?? 0) % 60).padStart(2, "0");
  return (
    <main className="assessment no-copy" data-testid="assessment-page" onCopy={stop} onCut={stop} onPaste={stop} onContextMenu={stop} onDragStart={stop}>
      <div className="assessment-top"><div><div className="section-kicker">ASSESSMENT / ATTEMPT {assessment.attempt_number}</div><h1>{assessment.title}</h1><p className="muted small" data-testid="assessment-progress">{answered} of {total} answered · {assessment.pass_score}% to pass</p></div>
        <div className={left !== null && left < 300 ? "timer warn" : "timer"} data-testid="assessment-timer"><Clock3 size={18} /> {mm}:{ss}</div></div>
      <div className="question-list">{assessment.questions.map((q, i) => (
        <section className="question" key={q.id} data-testid={`question-${i + 1}`}><div className="question-number">{String(i + 1).padStart(2, "0")}</div><h2>{q.prompt}</h2>
          <div className="options">{q.options.map((option, j) => (
            <label className={answers[q.id] === j ? "option selected" : "option"} data-testid={`question-${i + 1}-option-${j + 1}`} key={`${q.id}-${j}`}>
              <input type="radio" name={q.id} checked={answers[q.id] === j} onChange={() => setAnswers({ ...answers, [q.id]: j })} /><span>{String.fromCharCode(65 + j)}</span>{option}
            </label>))}</div>
        </section>))}</div>
      <button className="button primary submit-assessment" data-testid="submit-assessment-button" disabled={busy} onClick={confirmSubmit}>{busy ? "Submitting…" : "Submit assessment"} <ArrowRight size={17} /></button>
    </main>
  );
}
